/**
 * Centralized Country Configuration
 * Single source of truth for all country-related data across the app
 * 
 * This file defines:
 * - Supported countries for operator signup
 * - Phone codes for customer input
 * - Currency/timezone defaults per country
 * - Twilio/Stripe support flags
 */

// =============================================================================
// CORE COUNTRIES DATA
// =============================================================================

export const COUNTRIES = [
  {
    code: 'USA',
    name: 'United States',
    flag: '🇺🇸',
    currency: 'USD',
    currencySymbol: '$',
    phoneCode: '+1',
    phoneDigits: 10,  // Expected digits after country code
    phoneFormat: '(XXX) XXX-XXXX',
    phonePlaceholder: '(555) 555-5555',
    defaultTimezone: 'America/Chicago',
    taxType: 'sales_tax',
    defaultTaxRate: 8.25,
    twilioSmsSupported: true,
    twilioWhatsappSupported: false,
    stripeSupported: true,
    active: true,
    sortOrder: 1
  },
  {
    code: 'UAE',
    name: 'United Arab Emirates',
    flag: '🇦🇪',
    currency: 'AED',
    currencySymbol: 'د.إ',
    phoneCode: '+971',
    phoneDigits: 9,  // Expected digits after country code
    phoneFormat: 'XX XXX XXXX',
    phonePlaceholder: '50 123 4567',
    defaultTimezone: 'Asia/Dubai',
    taxType: 'vat',
    defaultTaxRate: 5.0,
    twilioSmsSupported: true,
    twilioWhatsappSupported: true,
    stripeSupported: true,
    active: true,
    sortOrder: 2
  },
  // Future countries - set active: false until ready to launch
  {
    code: 'GBR',
    name: 'United Kingdom',
    flag: '🇬🇧',
    currency: 'GBP',
    currencySymbol: '£',
    phoneCode: '+44',
    phoneDigits: 10,
    phoneFormat: 'XXXX XXXXXX',
    phonePlaceholder: '7911 123456',
    defaultTimezone: 'Europe/London',
    taxType: 'vat',
    defaultTaxRate: 20.0,
    twilioSmsSupported: true,
    twilioWhatsappSupported: true,
    stripeSupported: true,
    active: false,  // Not yet launched
    sortOrder: 3
  },
  {
    code: 'EUR',
    name: 'European Union',
    flag: '🇪🇺',
    currency: 'EUR',
    currencySymbol: '€',
    phoneCode: '+33',  // Default to France
    phoneDigits: 9,
    phoneFormat: 'X XX XX XX XX',
    phonePlaceholder: '6 12 34 56 78',
    defaultTimezone: 'Europe/Paris',
    taxType: 'vat',
    defaultTaxRate: 20.0,
    twilioSmsSupported: true,
    twilioWhatsappSupported: true,
    stripeSupported: true,
    active: false,
    sortOrder: 4
  },
  {
    code: 'AUS',
    name: 'Australia',
    flag: '🇦🇺',
    currency: 'AUD',
    currencySymbol: 'A$',
    phoneCode: '+61',
    phoneDigits: 9,
    phoneFormat: 'XXX XXX XXX',
    phonePlaceholder: '412 345 678',
    defaultTimezone: 'Australia/Sydney',
    taxType: 'gst',
    defaultTaxRate: 10.0,
    twilioSmsSupported: true,
    twilioWhatsappSupported: true,
    stripeSupported: true,
    active: false,
    sortOrder: 5
  }
];

// =============================================================================
// PHONE CODES (For customer phone input dropdowns)
// =============================================================================

// All phone codes including inactive countries (for customer input flexibility)
export const ALL_PHONE_CODES = COUNTRIES.map(c => ({
  code: c.phoneCode,
  country: c.code,
  countryName: c.name,
  flag: c.flag,
  digits: c.phoneDigits,
  format: c.phoneFormat,
  placeholder: c.phonePlaceholder,
  smsSupported: c.twilioSmsSupported,
  whatsappSupported: c.twilioWhatsappSupported
}));

// Only phone codes where Twilio SMS works (for guaranteed delivery)
export const SUPPORTED_PHONE_CODES = ALL_PHONE_CODES.filter(p => p.smsSupported);

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get active countries only (for operator signup)
 */
export const getActiveCountries = () => {
  return COUNTRIES.filter(c => c.active).sort((a, b) => a.sortOrder - b.sortOrder);
};

/**
 * Get country by code
 * @param {string} code - Country code (e.g., 'USA', 'UAE')
 */
export const getCountryByCode = (code) => {
  return COUNTRIES.find(c => c.code === code);
};

/**
 * Get country by phone code
 * @param {string} phoneCode - Phone code (e.g., '+1', '+971')
 */
export const getCountryByPhoneCode = (phoneCode) => {
  return COUNTRIES.find(c => c.phoneCode === phoneCode);
};

/**
 * Get currency for a country
 * @param {string} countryCode - Country code
 */
export const getCurrencyForCountry = (countryCode) => {
  const country = getCountryByCode(countryCode);
  return country?.currency || 'USD';
};

/**
 * Get default timezone for a country
 * @param {string} countryCode - Country code
 */
export const getTimezoneForCountry = (countryCode) => {
  const country = getCountryByCode(countryCode);
  return country?.defaultTimezone || 'America/Chicago';
};

/**
 * Get phone code for a country
 * @param {string} countryCode - Country code
 */
export const getPhoneCodeForCountry = (countryCode) => {
  const country = getCountryByCode(countryCode);
  return country?.phoneCode || '+1';
};

/**
 * Smart phone code from currency (for widget)
 * @param {string} currency - Currency code (e.g., 'USD', 'AED')
 */
export const getPhoneCodeFromCurrency = (currency) => {
  const country = COUNTRIES.find(c => c.currency === currency);
  return country?.phoneCode || '+1';
};

/**
 * Format phone number for display
 * @param {string} phone - Raw phone number
 * @param {string} countryCode - Country code for formatting rules
 */
export const formatPhoneDisplay = (phone, countryCode = 'USA') => {
  if (!phone) return '';
  
  // Remove all non-digits
  const digits = phone.replace(/\D/g, '');
  const country = getCountryByCode(countryCode);
  
  if (!country) return phone;
  
  if (countryCode === 'USA') {
    // Format: +1 (555) 123-4567
    if (digits.length >= 10) {
      const num = digits.slice(-10);
      return `${country.phoneCode} (${num.slice(0, 3)}) ${num.slice(3, 6)}-${num.slice(6)}`;
    }
  } else if (countryCode === 'UAE') {
    // Format: +971 50 123 4567
    if (digits.length >= 9) {
      const num = digits.slice(-9);
      return `${country.phoneCode} ${num.slice(0, 2)} ${num.slice(2, 5)} ${num.slice(5)}`;
    }
  }
  
  return `${country.phoneCode} ${digits}`;
};

/**
 * Sanitize phone to E.164 format
 * @param {string} phone - Raw phone input
 * @param {string} phoneCode - Country phone code (e.g., '+1')
 */
export const sanitizePhoneE164 = (phone, phoneCode = '+1') => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  // Remove leading country code if already present
  const cleanPhoneCode = phoneCode.replace('+', '');
  const cleanDigits = digits.startsWith(cleanPhoneCode) 
    ? digits.slice(cleanPhoneCode.length) 
    : digits;
  return `${phoneCode}${cleanDigits}`.substring(0, 20);
};

// =============================================================================
// COUNTRY OPTIONS FOR DROPDOWNS
// =============================================================================

/**
 * Get country options for signup dropdown
 * Returns only active countries formatted for <select>
 */
export const getCountryOptions = () => {
  return getActiveCountries().map(c => ({
    value: c.code,
    label: `${c.flag} ${c.name} (${c.currency})`,
    currency: c.currency
  }));
};

/**
 * Get phone code options for phone input dropdown
 * Returns all supported phone codes formatted for <select>
 */
export const getPhoneCodeOptions = () => {
  return SUPPORTED_PHONE_CODES.map(p => ({
    value: p.code,
    label: `${p.flag} ${p.code}`,
    country: p.country,
    smsSupported: p.smsSupported
  }));
};

// =============================================================================
// DEFAULTS
// =============================================================================

export const DEFAULT_COUNTRY = 'USA';
export const DEFAULT_PHONE_CODE = '+1';
export const DEFAULT_CURRENCY = 'USD';
